import axios, { AxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

interface IStoreTradingEpochRewardsResponse {
  success?: boolean;
  message?: string;
  epoch?: number;
  records_stored?: number;
}

interface IStoreTradingEpochRewardsError {
  error?: string;
  code?: string;
  message?: string;
}

export const useStoreTradingEpochRewards = () => {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const storeTradingEpochRewards = useCallback(async (): Promise<boolean> => {
    if (!tokenId) {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Authentication Required",
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
      return false;
    }

    setIsLoading(true);

    try {
      const response = await axios.get<IStoreTradingEpochRewardsResponse>(
        ADMIN_API_URL("generate-emissions"),
        {
          ...queryConfig(),
          params: { mode: "store" },
        }
      );

      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Trading Epoch Rewards Stored",
          subheader:
            response.data.message ||
            `Successfully stored trading rewards for epoch ${
              response.data.epoch || "unknown"
            }`,
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
      return true;
    } catch (error) {
      const axiosError = error as AxiosError<IStoreTradingEpochRewardsError>;
      const statusCode = axiosError.response?.status;
      const errorData = axiosError.response?.data;

      let errorHeader = "Failed to Store Trading Rewards";
      let errorMessage = "An unknown error occurred";

      if (statusCode === 400 && errorData?.code === "EPOCH_TOO_EARLY") {
        errorHeader = "Epoch Too Early";
        errorMessage = "Only epoch 3 and above can be stored";
      } else if (
        statusCode === 409 &&
        errorData?.code === "EPOCH_ALREADY_STORED"
      ) {
        errorHeader = "Epoch Already Stored";
        errorMessage =
          "Trading rewards for this epoch have already been stored";
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }

      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: errorHeader,
          subheader: errorMessage,
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [tokenId, queryConfig, addToast]);

  return { storeTradingEpochRewards, isLoading };
};
